// Single source of truth for the pricing tiers shown in the UI.
//
// Consumers: Landing/LandingPage.js (full three-card grid),
// Festival/NykfOfferPage.js (compact two-plan strip), Admin pages (PLAN_LABELS).
//
// ⚠️ PRICE PROSE LIVES IN FIVE OTHER PLACES that cannot import this file, because
// they are either server-side or evaluated outside the bundler:
//   • Festival/NykfOfferPage.js  → TERMS[] and FAQS[] (hand-written prose)
//   • common/bookCallVoiceConfig.js → the voice agent's system prompt. This file
//     MUST stay import-free: scripts/voice-agent-sim.js strips `export` and evals it.
//   • routes/contactRoutes.js    → the text-chat intake prompt
//   • services/studioEmailService.js → PLAN_LABELS for notification emails
// A retired $249 tier once survived in all five for weeks. GREP FOR THE PRICE
// before you change one.

// Demo videos live in S3, not the repo: ~8MB of MP4 would ship in every build and
// slow first load. Stable filenames + a year of immutable caching, so a visitor
// downloads each demo once. NOTE: to replace a demo, upload under a NEW filename —
// overwriting leaves returning visitors on the cached copy. The bucket is allowed
// in the Helmet CSP mediaSrc (server.js); without that the browser blocks the video.
export const DEMO_BASE = 'https://dancehall-studios-assets.s3.amazonaws.com/demo';

export const DEMOS = {
  bookings: {
    src: `${DEMO_BASE}/bookings-demo.mp4`,
    // Posters are served from our own origin, right-sized (the S3 original was
    // 882x1920 for a ~330x717 slot — PageSpeed's biggest image finding). Videos stay on S3.
    poster: '/brand/demo/bookings-demo-poster.jpg',
    orientation: 'portrait',
    label: 'Watch the onboarding demo',
    title: 'Studio — from your schedule to a paid booking page',
  },
  studio: {
    src: `${DEMO_BASE}/studio-demo.mp4`,
    poster: '/brand/demo/studio-demo-poster.jpg',
    orientation: 'landscape',
    label: 'Watch the onboarding demo',
    title: 'Studio Pro — run your whole school',
  },
};

export const PLANS = [
  {
    key: 'invoicing', name: 'Studio', price: '$49', tagline: 'Take bookings, get paid',
    // The only self-serve tier: its Get Started goes to /start (signup → onboarding
    // → plan) instead of the book-a-call popup. $149 and Custom still start on a call.
    selfServe: true,
    highlight: false, intro: null, demo: DEMOS.bookings,
    features: [
      ['Booking page + payment links', 'Share one page; students pick a class or package and pay.'],
      ['Online & in-person', 'Take bookings for online (Zoom), in-person, or hybrid classes.'],
      ['Invoices by email + WhatsApp', 'Send an invoice — they pay in a tap.'],
      ['Sangs, your AI admin', 'Your AI assistant — answers questions and chases unpaid invoices for you.'],
    ],
  },
  {
    key: 'studio', name: 'Studio Pro', price: '$149', tagline: 'Run your studio',
    highlight: true, intro: 'Everything in Studio, plus', demo: DEMOS.studio,
    features: [
      ['Students & calendar, managed', 'Attendance, materials, reminders, promotions and pauses — with your studio and personal calendars synced so Sangs keeps you on schedule.'],
      ['Collect for anything', 'Studio & costume rental, digital downloads (PDFs), merch — beyond just classes.'],
      ['Communications', 'WhatsApp + email to students and parents.'],
    ],
  },
  {
    // Deliberately no number: this tier is scoped on a call. Renaming the KEY would
    // orphan existing BookingLead docs — `nonprofit` is a stored enum value.
    key: 'nonprofit', name: 'Custom', price: null, priceLabel: 'Let’s talk',
    cta: 'Talk to the team', tagline: 'Run your whole org', highlight: false,
    // Opens /start/custom as a plain ACCOUNT-CREATION link, for an org whose terms
    // were already agreed on a call. Deliberately NOT `selfServe`: LandingPage keys
    // this card's CTA off that flag, so setting it there would replace "Talk to the
    // team" with a signup link and skip lead capture. It also carries no billing —
    // see SelfServeStart, where only `selfServe` appends ?plan=studio_flat.
    signupOnly: true,
    intro: 'Everything in Studio, plus', demo: null,
    features: [
      // Ordered by what an org actually buys on: the bespoke build first,
      // donors next, then reporting, with campaigns last.
      ['Custom build', 'We build what your organization specifically needs.'],
      ['Donor tracking & management', 'Track donors and their giving — and sell tickets to recitals & shows.'],
      ['All your reporting in one place', 'Board-ready reporting across classes, payments and donors — plus grants you can apply for.'],
      ['Email campaigns', 'School-wide campaigns and newsletters from your own sender.'],
    ],
  },
];

export const PLAN_BY_KEY = Object.fromEntries(PLANS.map((p) => [p.key, p]));

// ── Self-serve signup ──────────────────────────────────────────────────────────
//
// THREE separate identifiers, deliberately. Collapsing them is what produced the
// mess this replaced — the key `studio` was the $149 plan while the card actually
// named "Studio" carried the key `invoicing`.
//
//   1. `key`  — the INTERNAL storage value. Frozen: it is a stored enum on
//               BookingLead.plan / .clickedPlan (models/BookingLead.js:35,41) with
//               live rows behind it, and it is echoed by contactRoutes and the
//               voice-agent extractor. Renaming it orphans those documents.
//   2. `slug` — the PUBLIC URL segment (/start/:slug). Hyphenated, lowercase, and
//               named after what the customer actually bought. This is the one
//               that ends up in UTM links, printed QR codes and email campaigns,
//               so it must stay stable and readable — hyphens, never underscores
//               (search engines treat `_` as a word-joiner and `-` as a separator).
//   3. tier    — the SERVER-side entitlement value in config/plans.js.
//
// The plan travels in the URL rather than in state because the client-side approval
// gate in App.js has to see it across a page load: a brand-new teacher is
// status:'pending' until their studio exists, so without a signal in the route the
// guard sends a self-serve signup to "Your account is under review".
export const SELF_SERVE_BILLING_PLAN = 'studio_flat';

const PLAN_SLUGS = {
  invoicing: 'studio-basic',   // $49
  studio: 'studio-pro',        // $149
  nonprofit: 'custom',
};

export const planSlug = (key) => PLAN_SLUGS[key] || key;

// Resolve a URL segment to a plan that /start may sign someone up for. Accepts the
// slug (canonical) and the legacy internal key, so a /start/invoicing link already in
// the wild keeps working instead of 404ing — signup URLs outlive the naming decisions
// behind them. Two flags qualify, and they are NOT the same thing: `selfServe` is a
// tier anyone may buy unattended, `signupOnly` is a login for a deal closed off-platform.
// Anything else returns null and /start sends the visitor back to the pricing section.
export const getSelfServePlan = (segment) => {
  if (!segment) return null;
  const s = String(segment).toLowerCase();
  const bySlug = PLANS.find((p) => PLAN_SLUGS[p.key] === s);
  const plan = bySlug || PLAN_BY_KEY[s];
  return (plan?.selfServe || plan?.signupOnly) ? plan : null;
};

// What a bare /start resolves to.
export const DEFAULT_SELF_SERVE_SLUG = planSlug(PLANS.find((p) => p.selfServe)?.key || '');

// Short labels for internal surfaces (admin lists). Mirrors PLAN_LABELS in
// services/studioEmailService.js — that copy is server-side and can't import this.
export const PLAN_LABELS = {
  invoicing: 'Studio ($49)',
  studio: 'Studio Pro ($149)',
  nonprofit: 'Custom (scope on the call)',
  unsure: 'Not sure yet',
};
